import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/api';
import AppHeader from './Header/AppHeader';
import './PricingPage.css';

function PricingPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('annual'); // 'monthly' or 'annual'

  const handleSubscribe = (type) => {
    if (!user) {
      navigate('/login');
      return;
    }

    const planName = type === 'standard' 
      ? (billingInterval === 'monthly' ? 'Standard Monatlich' : 'Standard Jährlich')
      : (billingInterval === 'monthly' ? 'Premium Monatlich' : 'Premium Jährlich');

    const amount = type === 'standard' 
      ? (billingInterval === 'monthly' ? prices.standard.monthly : prices.standard.annual)
      : (billingInterval === 'monthly' ? prices.premium.monthly : prices.premium.annual);

    // Navigate to checkout page with order details
    navigate('/checkout', {
      state: {
        orderDetails: {
          tier: type,
          planName,
          amount,
          interval: billingInterval
        }
      }
    });
  };

  const prices = {
    standard: {
      monthly: 29.95,
      annual: 299.95,
      savings: 59.45  // 12 * 29.95 = 359.40 - 299.95 = 59.45
    },
    premium: {
      monthly: 49.95,
      annual: 499.95,
      savings: 99.45  // 12 * 49.95 = 599.40 - 499.95 = 99.45
    }
  };

  const isGerman = i18n.language === 'de';

  return (
    <>
      <AppHeader />
      <div className="pricing-page">
        <div className="pricing-container">
        <div className="pricing-header">
          <h1 className="pricing-title">{t('pricing.title')}</h1>
          <p className="pricing-subtitle">{t('pricing.subtitle')}</p>
        </div>

        {/* Billing Toggle */}
        <div className="billing-toggle">
          <button
            className={`toggle-btn ${billingInterval === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingInterval('monthly')}
          >
            {t('pricing.monthly')}
          </button>
          <button
            className={`toggle-btn ${billingInterval === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingInterval('annual')}
          >
            {t('pricing.yearly')}
            <span className="toggle-savings">{isGerman ? 'Spare bis zu 17%' : 'Save up to 17%'}</span>
          </button>
        </div>

        <div className="plans-grid-two">
          {/* Standard Plan */}
          <div className="plan-card">
            <div className="plan-header">
              <h3 className="plan-name">{t('pricing.standard.title')}</h3>
              <div className="plan-price">
                <span className="price-amount">{billingInterval === 'monthly' ? prices.standard.monthly : prices.standard.annual}</span>
                <span className="price-currency">€{billingInterval === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}</span>
              </div>
              {billingInterval === 'annual' && (
                <div className="plan-savings">{t('pricing.save', { amount: prices.standard.savings })}</div>
              )}
            </div>
            <ul className="plan-features">
              <li>✅ {t('pricing.standard.features.frequencies')}</li>
              <li>✅ {t('pricing.standard.features.programs')}</li>
              <li>✅ {isGerman ? 'Audio-Bibliothek' : 'Audio library'}</li>
              <li>✅ {t('pricing.standard.features.journal')}</li>
              <li>✅ {isGerman ? 'Automatische Routinen' : 'Automatic routines'}</li>
              <li>👤 {isGerman ? '1 Account' : '1 account'}</li>
            </ul>
            <button
              onClick={() => handleSubscribe('standard')}
              className="btn-subscribe"
              disabled={loading && selectedPlan === 'standard'}
              data-testid="pricing-standard-subscribe"
            >
              {loading && selectedPlan === 'standard' ? t('common.loading') : t('pricing.startTrial')}
            </button>
          </div>

          {/* Premium Plan */}
          <div className="plan-card premium featured">
            <div className="plan-badge premium">{isGerman ? 'Beste Wahl für Familien' : 'Best choice for families'}</div>
            <div className="plan-header">
              <h3 className="plan-name">{t('pricing.premium.title')}</h3>
              <div className="plan-price">
                <span className="price-amount">{billingInterval === 'monthly' ? prices.premium.monthly : prices.premium.annual}</span>
                <span className="price-currency">€{billingInterval === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}</span>
              </div>
              {billingInterval === 'annual' && (
                <div className="plan-savings">{t('pricing.save', { amount: prices.premium.savings })}</div>
              )}
            </div>
            <ul className="plan-features">
              <li>✅ {t('pricing.standard.features.frequencies')}</li>
              <li>✅ {t('pricing.standard.features.programs')}</li>
              <li>✅ {isGerman ? 'Audio-Bibliothek' : 'Audio library'}</li>
              <li>✅ {t('pricing.standard.features.journal')}</li>
              <li>✅ {isGerman ? 'Automatische Routinen' : 'Automatic routines'}</li>
              <li>👨‍👩‍👧‍👦 {isGerman ? 'Bis zu 5 Accounts' : 'Up to 5 accounts'}</li>
              <li>⭐ {isGerman ? 'Premium Support' : 'Premium support'}</li>
            </ul>
            <button
              onClick={() => handleSubscribe('premium')}
              className="btn-subscribe premium featured"
              disabled={loading && selectedPlan === 'premium'}
              data-testid="pricing-premium-subscribe"
            >
              {loading && selectedPlan === 'premium' ? t('common.loading') : (isGerman ? 'Premium starten' : 'Start Premium')}
            </button>
          </div>
        </div>

        <div className="pricing-guarantee">
          ✅ {isGerman ? '30 Tage Geld-zurück-Garantie' : '30-day money-back guarantee'} &nbsp;•&nbsp; 🔒 {isGerman ? 'Sichere Zahlung mit PayPal' : 'Secure payment with PayPal'}
        </div>
      </div>
    </div>
    </>
  );
}

export default PricingPage;
